import React, { useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Select, Switch, message, Space } from 'antd';
import { logger } from '@/utils/logger';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { ApiResponse } from '@/types/api';
import * as Icons from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

interface InfoBlock {
    id: number;
    title: string;
    description: string | null;
    link: string | null;
    icon: string | null;
    order: number;
    status: 'active' | 'inactive';
    createdAt: string;
    updatedAt: string;
}

interface CreateEditInfoBlockModalProps {
    visible: boolean;
    block: InfoBlock | null;
    onCancel: () => void;
    onSuccess: () => void;
}

// Список популярных иконок из Ant Design
const availableIcons = [
    'TrophyOutlined',
    'TeamOutlined',
    'UserOutlined',
    'CalendarOutlined',
    'FileTextOutlined',
    'SettingOutlined',
    'BankOutlined',
    'TagsOutlined',
    'DollarOutlined',
    'EnvironmentOutlined',
    'ClockCircleOutlined',
    'InfoCircleOutlined',
    'CheckCircleOutlined',
    'ExclamationCircleOutlined',
    'QuestionCircleOutlined',
    'HomeOutlined',
    'MenuOutlined',
    'AppstoreOutlined',
    'MailOutlined',
    'PhoneOutlined',
    'GlobalOutlined',
    'LinkOutlined',
    'PictureOutlined',
    'VideoCameraOutlined',
    'SoundOutlined',
    'BookOutlined',
    'ReadOutlined',
    'FileOutlined',
    'FolderOutlined',
    'DatabaseOutlined',
    'CloudOutlined',
    'BulbOutlined',
    'FireOutlined',
    'ThunderboltOutlined',
    'HeartOutlined',
    'StarOutlined',
    'LikeOutlined',
    'DislikeOutlined',
    'MessageOutlined',
    'CommentOutlined',
    'ShareAltOutlined',
    'RetweetOutlined',
    'DownloadOutlined',
    'UploadOutlined',
    'EditOutlined',
    'DeleteOutlined',
    'PlusOutlined',
    'MinusOutlined',
    'CloseOutlined',
    'CheckOutlined',
    'RightOutlined',
    'LeftOutlined',
    'UpOutlined',
    'DownOutlined',
];

export const CreateEditInfoBlockModal: React.FC<CreateEditInfoBlockModalProps> = ({
    visible,
    block,
    onCancel,
    onSuccess
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            if (block) {
                form.setFieldsValue({
                    title: block.title,
                    description: block.description,
                    link: block.link,
                    icon: block.icon,
                    order: block.order,
                    status: block.status === 'active'
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    status: true,
                    order: undefined
                });
            }
        }
    }, [visible, block, form]);


    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                title: values.title,
                description: values.description || null,
                link: values.link || null,
                icon: values.icon || null,
                order: values.order || undefined,
                status: values.status ? 'active' : 'inactive'
            };

            if (block) {
                // Обновление существующего блока
                await apiService.put<ApiResponse<InfoBlock>>(
                    API_ENDPOINTS.INFO_BLOCKS.UPDATE(block.id),
                    payload
                );
                message.success('Блок информации успешно обновлен');
            } else {
                // Создание нового блока
                await apiService.post<ApiResponse<InfoBlock>>(
                    API_ENDPOINTS.INFO_BLOCKS.CREATE,
                    payload
                );
                message.success('Блок информации успешно создан');
            }

            onSuccess();
        } catch (error: any) {
            logger.error('Error saving info block:', error);
            if (error?.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Ошибка при сохранении блока информации');
            }
        }
    };

    const renderIconPreview = (iconName: string | null) => {
        if (!iconName) return null;
        const IconComponent = (Icons as any)[iconName];
        if (IconComponent) {
            return <IconComponent style={{ fontSize: 20, marginLeft: 8 }} />;
        }
        return null;
    };

    return (
        <Modal
            title={block ? 'Редактировать блок информации' : 'Создать блок информации'}
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText={block ? 'Сохранить' : 'Создать'}
            cancelText="Отмена"
            width={600}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    status: true
                }}
            >
                <Form.Item
                    label="Название"
                    name="title"
                    rules={[
                        { required: true, message: 'Пожалуйста, введите название блока' },
                        { max: 255, message: 'Название не должно превышать 255 символов' }
                    ]}
                >
                    <Input placeholder="Введите название блока" />
                </Form.Item>

                <Form.Item
                    label="Описание"
                    name="description"
                    rules={[
                        { max: 2000, message: 'Описание не должно превышать 2000 символов' }
                    ]}
                >
                    <TextArea
                        rows={4}
                        placeholder="Введите описание блока (необязательно)"
                    />
                </Form.Item>

                <Form.Item
                    label="Ссылка"
                    name="link"
                    tooltip="Внутренняя ссылка начинается с / (например, /tournaments), внешняя - с http:// или https://"
                    rules={[
                        {
                            validator: (_rule, value) => {
                                if (!value || value.trim() === '') {
                                    return Promise.resolve();
                                }
                                const trimmed = value.trim();
                                if (trimmed.startsWith('/') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                                    return Promise.resolve();
                                }
                                return Promise.reject('Ссылка должна начинаться с / (внутренняя) или http(s):// (внешняя)');
                            }
                        }
                    ]}
                >
                    <Input placeholder="/tournaments или https://example.com" />
                </Form.Item>

                <Form.Item
                    label="Иконка"
                    name="icon"
                    tooltip="Выберите иконку из Ant Design Icons"
                >
                    <Select
                        placeholder="Выберите иконку"
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                            const value = option?.value;
                            if (typeof value === 'string') {
                                return value.toLowerCase().includes(input.toLowerCase());
                            }
                            return false;
                        }}
                    >
                        {availableIcons.map(iconName => {
                            const IconComponent = (Icons as any)[iconName];
                            return (
                                <Option key={iconName} value={iconName}>
                                    <Space>
                                        {IconComponent && <IconComponent />}
                                        <span>{iconName}</span>
                                    </Space>
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>
                <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.icon !== currentValues.icon} noStyle>
                    {({ getFieldValue }) => {
                        const currentIcon = getFieldValue('icon');
                        if (!currentIcon) return null;
                        return (
                            <div style={{ marginBottom: 16, padding: '8px', background: '#f5f5f5', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>Предпросмотр:</span>
                                {renderIconPreview(currentIcon)}
                                <span style={{ color: '#666', fontSize: '12px' }}>{currentIcon}</span>
                            </div>
                        );
                    }}
                </Form.Item>

                <Form.Item
                    label="Порядок отображения"
                    name="order"
                    tooltip="Число для сортировки блоков (меньшее число отображается первым)"
                    rules={[
                        { type: 'number', min: 0, message: 'Порядок должен быть неотрицательным числом' }
                    ]}
                >
                    <InputNumber
                        min={0}
                        placeholder="Автоматически, если не указано"
                        style={{ width: '100%' }}
                    />
                </Form.Item>

                <Form.Item
                    label="Статус"
                    name="status"
                    valuePropName="checked"
                >
                    <Switch
                        checkedChildren="Активен"
                        unCheckedChildren="Неактивен"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
